import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigation } from 'react-router';
import { dashboardV3NavItems, type NavItem } from '@/lib/navigation';
import { isNavPathActive, ownerPath } from '@/lib/hosts';

/**
 * SubNavContext — Phase 8 (HeyGen-style persistent sub-nav).
 *
 * State machine :
 * - `closed`    : no panel, no pill. (e.g. user is on /dashboard with no module
 *                  currently selected).
 * - `open`      : panel slides next to the rail (256px). Content area gets
 *                  pushed right.
 * - `collapsed` : panel hidden, but a compact pill is shown in the top-left of
 *                  the content area letting the user re-open it.
 *
 * Auto-open on route change : if the current pathname matches a navItem with
 * children, the subnav auto-opens for that item (unless the user previously
 * collapsed it — collapsed state is sticky).
 */

export type SubNavState = 'closed' | 'open' | 'collapsed';

interface SubNavContextValue {
  state: SubNavState;
  /** labelKey of the rail item whose sub-nav is currently displayed (or last shown). */
  activeKey: string | null;
  /** The currently active NavItem with its children (resolved from `activeKey`). */
  activeItem: NavItem | null;
  /** Smart toggle from a rail item click. */
  toggleFromRail: (item: NavItem) => void;
  /** Force-open the panel (used by the collapsed pill). */
  open: () => void;
  /** Force-collapse the panel (used by the panel's close button). */
  collapse: () => void;
  /** Reset to closed (e.g. user navigates to a route with no parent module). */
  close: () => void;
}

const SubNavContext = createContext<SubNavContextValue | null>(null);

const STORAGE_KEY = 'velko:subnav-state';

interface StoredState {
  state: SubNavState;
  activeKey: string | null;
}

function readStored(): StoredState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.activeKey === 'string' &&
      (parsed.state === 'closed' || parsed.state === 'open' || parsed.state === 'collapsed')
    ) {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStored(state: SubNavState, activeKey: string | null) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, activeKey }));
  } catch {
    // ignore (quota / privacy mode)
  }
}

/** Find the navItem (with children) whose href matches the current pathname. */
function findItemForPathname(pathname: string): NavItem | null {
  for (const item of dashboardV3NavItems) {
    if (!item.children || item.children.length === 0) continue;
    // Match either parent href or any child href as a prefix.
    const parentMatch = isNavPathActive(pathname, ownerPath(item.href));
    if (parentMatch) return item;
    const childMatch = item.children.some((c) => {
      const target = ownerPath(c.href);
      return isNavPathActive(pathname, target);
    });
    if (childMatch) return item;
  }
  return null;
}

interface SubNavProviderProps {
  children: ReactNode;
}

export function SubNavProvider({ children }: SubNavProviderProps) {
  const { pathname } = useLocation();
  const navigation = useNavigation();
  const effectivePathname = navigation.location?.pathname ?? pathname;
  const stored = useMemo(() => readStored(), []);
  const [state, setState] = useState<SubNavState>(stored?.state ?? 'closed');
  const [activeKey, setActiveKey] = useState<string | null>(stored?.activeKey ?? null);

  // Auto-resolve activeKey from current route.
  useEffect(() => {
    const matched = findItemForPathname(effectivePathname);
    if (matched) {
      // We're inside a module with children.
      setActiveKey(matched.labelKey);
      // If we were closed, open. If we were collapsed, stay collapsed (sticky).
      setState((prev) => (prev === 'closed' ? 'open' : prev));
    } else {
      // We're on a leaf route (dashboard, etc.). Keep current state but blank
      // the active item so the pill / panel disappears.
      setActiveKey(null);
      setState('closed');
    }
  }, [effectivePathname]);

  // Persist.
  useEffect(() => {
    writeStored(state, activeKey);
  }, [state, activeKey]);

  const activeItem = useMemo<NavItem | null>(() => {
    if (!activeKey) return null;
    return dashboardV3NavItems.find((i) => i.labelKey === activeKey) ?? null;
  }, [activeKey]);

  const toggleFromRail = useCallback(
    (item: NavItem) => {
      const hasChildren = (item.children?.length ?? 0) > 0;
      if (!hasChildren) {
        // Visibility can react to the click; module identity remains derived
        // from React Router's effective destination.
        setState('closed');
        return;
      }
      if (activeKey === item.labelKey) {
        // Same module — toggle open ↔ collapsed.
        setState((prev) => (prev === 'open' ? 'collapsed' : 'open'));
      } else {
        // React Router's pending/committed route will switch activeKey.
        setState('open');
      }
    },
    [activeKey],
  );

  const open = useCallback(() => setState('open'), []);
  const collapse = useCallback(() => setState('collapsed'), []);
  const close = useCallback(() => {
    setState('closed');
  }, []);

  const value = useMemo<SubNavContextValue>(
    () => ({ state, activeKey, activeItem, toggleFromRail, open, collapse, close }),
    [state, activeKey, activeItem, toggleFromRail, open, collapse, close],
  );

  return <SubNavContext.Provider value={value}>{children}</SubNavContext.Provider>;
}

export function useSubNav(): SubNavContextValue {
  const ctx = useContext(SubNavContext);
  if (!ctx) {
    // Graceful fallback : if a component using this hook is mounted outside
    // the provider (e.g. unit tests), return a stable no-op value rather than
    // throwing — UX impact is just "no subnav" which is harmless.
    return {
      state: 'closed',
      activeKey: null,
      activeItem: null,
      toggleFromRail: () => {},
      open: () => {},
      collapse: () => {},
      close: () => {},
    };
  }
  return ctx;
}
